import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

const TIMEZONES = [
  { label: 'Europe', options: [
    'Europe/Amsterdam','Europe/Berlin','Europe/Brussels','Europe/Copenhagen',
    'Europe/Dublin','Europe/Helsinki','Europe/Lisbon','Europe/London',
    'Europe/Madrid','Europe/Oslo','Europe/Paris','Europe/Prague',
    'Europe/Rome','Europe/Stockholm','Europe/Vienna','Europe/Warsaw',
    'Europe/Athens','Europe/Bucharest','Europe/Istanbul',
  ]},
  { label: 'Americas', options: [
    'America/New_York','America/Chicago','America/Denver','America/Los_Angeles',
    'America/Toronto','America/Vancouver','America/Mexico_City',
    'America/Sao_Paulo','America/Argentina/Buenos_Aires','America/Bogota',
  ]},
  { label: 'Asia / Pacific', options: [
    'Asia/Dubai','Asia/Kolkata','Asia/Bangkok','Asia/Singapore',
    'Asia/Shanghai','Asia/Tokyo','Asia/Seoul',
    'Australia/Sydney','Australia/Melbourne','Pacific/Auckland',
  ]},
  { label: 'Africa / Middle East', options: [
    'Africa/Cairo','Africa/Johannesburg','Africa/Lagos','Africa/Nairobi',
    'Asia/Riyadh','Asia/Beirut',
  ]},
  { label: 'UTC', options: ['UTC'] },
];

const LOCKABLE_TABS = [
  { key: 'list', label: 'Reservations' },
  { key: 'tables',       label: 'Tables' },
  { key: 'menu',         label: 'Menu' },
  { key: 'analytics',    label: 'Analytics' },
  { key: 'waitlist',     label: 'Waitlist' },
  { key: 'settings',     label: 'Settings' },
];

export default function Settings() {
  const [settings, setS] = useState(null);
  const [totalSeats, setTotalSeats] = useState(null);
  const originalRef = useRef(null);
  const [saving, setSave] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Account email
  const [accountEmail, setAccountEmail] = useState('');
  const [emailSaving, setEmailSaving]   = useState(false);
  const [emailSaved, setEmailSaved]     = useState(false);
  const [emailError, setEmailError]     = useState('');

  // Tab lock / owner password
  const [ownerPwdForm, setOwnerPwdForm] = useState({ current: '', next: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdOk, setPwdOk] = useState(false);
  const [hasOwnerPwd, setHasOwnerPwd] = useState(false);
  const [confirmLockPwd, setConfirmLockPwd] = useState('');
  const [confirmLockErr, setConfirmLockErr] = useState('');
  const [showLockConfirm, setShowLockConfirm] = useState(false);
  const [confirmSaving, setConfirmSaving] = useState(false);

  useEffect(()=>{
    api.getSettings().then(s => { setS(s); originalRef.current = JSON.stringify(s); }).catch(()=>setError('Failed to load settings.'));
    api.getAccount().then(a => setAccountEmail(a.email || '')).catch(()=>{});
    api.getTables().then(tables => {
      const active = tables.filter(t => t.is_active === 1);
      setTotalSeats(active.reduce((sum, t) => sum + t.capacity, 0));
    }).catch(()=>{});
    // Check if owner password is already set
    api.verifyOwnerPassword('').then(r => setHasOwnerPwd(!r.valid)).catch(()=>{});
  },[]);

  const setField = (k,v) => setS(s=>({...s,[k]:v}));
  const setHours = (day,field,value) => setS(s=>({...s,opening_hours:{...s.opening_hours,[day]:{...s.opening_hours[day],[field]:value}}}));

  const lockedTabsArray = () => {
    const v = settings?.locked_tabs;
    if (!v) return [];
    if (Array.isArray(v)) return v;
    try { return JSON.parse(v); } catch { return []; }
  };
  const toggleLockedTab = (key) => {
    const current = lockedTabsArray();
    const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
    setField('locked_tabs', next);
  };

  const saveAccountEmail = async () => {
    setEmailError(''); setEmailSaving(true);
    try {
      await api.updateAccountEmail(accountEmail);
      setEmailSaved(true);
      setTimeout(() => setEmailSaved(false), 2500);
    } catch (err) { setEmailError(err.message || 'Failed to save email'); }
    finally { setEmailSaving(false); }
  };

  const saveOwnerPassword = async () => {
    setPwdError(''); setPwdOk(false);
    if (!ownerPwdForm.next || ownerPwdForm.next.length < 4) { setPwdError('Password must be at least 4 characters'); return; }
    if (ownerPwdForm.next !== ownerPwdForm.confirm) { setPwdError('Passwords do not match'); return; }
    setPwdSaving(true);
    try {
      await api.setOwnerPassword(ownerPwdForm.current, ownerPwdForm.next);
      setPwdOk(true);
      setHasOwnerPwd(true);
      setOwnerPwdForm({ current: '', next: '', confirm: '' });
      setTimeout(() => setPwdOk(false), 3000);
    } catch (err) { setPwdError(err.message); }
    finally { setPwdSaving(false); }
  };

  const doSave = async () => {
    setSave(true); setError('');
    try {
      await api.updateSettings(settings);
      originalRef.current = JSON.stringify(settings);
      setSaved(true);
      setTimeout(()=>setSaved(false),2500);
      window.dispatchEvent(new CustomEvent('seva:settings-saved'));
    }
    catch(err){ setError(err.message); } finally { setSave(false); }
  };

  const save = async e => {
    e.preventDefault();
    // If new tabs are being locked, require owner password confirmation
    const origLocked = (() => { try { const o = JSON.parse(originalRef.current || '{}'); const v = o.locked_tabs; if (!v) return []; if (Array.isArray(v)) return v; return JSON.parse(v); } catch { return []; } })();
    const newLocked = lockedTabsArray();
    const hasNewLocks = newLocked.some(k => !origLocked.includes(k));
    const hasUnlocks  = origLocked.some(k => !newLocked.includes(k));
    if ((hasNewLocks || hasUnlocks) && hasOwnerPwd) {
      setConfirmLockPwd(''); setConfirmLockErr(''); setShowLockConfirm(true);
      return;
    }
    doSave();
  };

  const confirmAndSave = async () => {
    setConfirmLockErr(''); setConfirmSaving(true);
    try {
      const { valid } = await api.verifyOwnerPassword(confirmLockPwd);
      if (!valid) { setConfirmLockErr('Incorrect password'); return; }
      setShowLockConfirm(false);
      doSave();
    } catch { setConfirmLockErr('Could not verify password'); }
    finally { setConfirmSaving(false); }
  };

  const hasChanges = settings && JSON.stringify(settings) !== originalRef.current;

  if (!settings) return <div style={{color:'var(--text-muted)',padding:40}}>Loading settings…</div>;

  return (
    <div>
      <h1 style={s.h1}>Settings</h1>
      <p style={s.sub}>Configure your restaurant details, hours, notifications, and features.</p>

      <form onSubmit={save} style={s.form}>
        {error && <div style={s.error}>{error}</div>}
        {saved && <div style={s.ok}>Settings saved!</div>}

        {/* Account */}
        <div style={s.card}>
          <h2 style={s.ct}>Account</h2>
          <p style={s.hint}>Your contact email — used for password reset and username change links.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 320 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email address</label>
            <input
              style={{ ...s.input, background: 'var(--section-bg)', color: 'var(--text-muted)', cursor: 'not-allowed' }}
              type="email"
              readOnly
              value={accountEmail}
            />
            <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>To change your email, contact your admin.</p>
          </div>
        </div>

        {/* General */}
        <div style={s.card}>
          <h2 style={s.ct}>General</h2>
          <div style={s.row}>
            <Field label="Restaurant name">
              <input style={s.input} className="glass-input" type="text" value={settings.restaurant_name||''} onChange={e=>setField('restaurant_name',e.target.value)} />
            </Field>
            <Field label="Timezone">
              <select style={s.input} className="glass-input" value={settings.timezone||'Europe/Amsterdam'} onChange={e=>setField('timezone',e.target.value)}>
                {TIMEZONES.map(group => (
                  <optgroup key={group.label} label={group.label}>
                    {group.options.map(tz => (
                      <option key={tz} value={tz}>{tz.replace(/_/g,' ')}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </Field>
          </div>
          <div style={s.row}>
            <Field label="Slot duration (mins)">
              <input style={s.input} className="glass-input" type="number" min="15" step="15" value={settings.slot_duration||120} onChange={e=>setField('slot_duration',e.target.value)} />
            </Field>
            <Field label="Max party size">
              <input style={{...s.input, background:'var(--section-bg)', color:'var(--text-muted)', cursor:'not-allowed'}} readOnly value={totalSeats !== null ? totalSeats : '—'} title="Calculated from total active table seats" />
            </Field>
            <Field label="Max days in advance">
              <input style={s.input} className="glass-input" type="number" min="1" value={settings.advance_days||60} onChange={e=>setField('advance_days',e.target.value)} />
            </Field>
          </div>
        </div>

        {/* Opening hours */}
        <div style={s.card}>
          <h2 style={s.ct}>Opening hours</h2>
          <div style={s.hoursGrid}>
            {DAYS.map(day=>{
              const h=(settings.opening_hours||{})[day]||{open:'11:00',close:'22:00',closed:false};
              return (
                <div key={day} style={s.dayRow}>
                  <span style={s.dayName}>{day.charAt(0).toUpperCase()+day.slice(1)}</span>
                  <label style={s.closedLabel}>
                    <input type="checkbox" checked={!!h.closed} onChange={e=>setHours(day,'closed',e.target.checked)} style={{marginRight:6,accentColor:'var(--accent)'}} />
                    Closed
                  </label>
                  {!h.closed && (
                    <>
                      <input style={s.timeIn} className="glass-input" type="time" value={h.open} onChange={e=>setHours(day,'open',e.target.value)} />
                      <span style={{fontSize:12,color:'var(--text-muted)'}}>to</span>
                      <input style={s.timeIn} className="glass-input" type="time" value={h.close} onChange={e=>setHours(day,'close',e.target.value)} />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* AI & Language */}
        <div style={s.card}>
          <h2 style={s.ct}>AI & Language</h2>
          <p style={s.hint}>The AI chatbot will always reply in the customer's language. This sets the fallback when their language can't be detected.</p>
          <Field label="Restaurant primary language">
            <select style={s.input} className="glass-input" value={settings.primary_language||'English'} onChange={e=>setField('primary_language',e.target.value)}>
              {['English','Spanish','French','Italian','German','Portuguese','Dutch','Arabic','Japanese','Chinese (Simplified)','Chinese (Traditional)','Korean','Hindi','Turkish','Polish','Russian','Swedish','Norwegian','Danish','Finnish'].map(l =>
                <option key={l} value={l}>{l}</option>
              )}
            </select>
          </Field>
        </div>

        {/* Custom AI questions */}
        <div style={s.card}>
          <h2 style={s.ct}>Custom AI questions</h2>
          <p style={s.hint}>The AI will ask these questions during every booking, after collecting the standard details. Answers are saved in the reservation notes. All questions are optional for the customer.</p>
          <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:12}}>
            {(Array.isArray(settings.custom_ai_questions) ? settings.custom_ai_questions : []).map((q, i) => (
              <div key={i} style={{display:'flex',gap:8,alignItems:'center'}}>
                <input
                  style={{...s.input,flex:1}}
                  className="glass-input"
                  value={q}
                  placeholder={`e.g. Would you like to pre-order a bottle of wine?`}
                  onChange={e => {
                    const qs = [...(settings.custom_ai_questions || [])];
                    qs[i] = e.target.value;
                    setField('custom_ai_questions', qs);
                  }}
                />
                <button
                  type="button"
                  style={s.removeQBtn}
                  onClick={() => {
                    const qs = (settings.custom_ai_questions || []).filter((_, idx) => idx !== i);
                    setField('custom_ai_questions', qs);
                  }}
                >×</button>
              </div>
            ))}
          </div>
          <button
            type="button"
            style={s.addQBtn}
            onClick={() => setField('custom_ai_questions', [...(settings.custom_ai_questions || []), ''])}
          >
            + Add question
          </button>
        </div>

        {/* Features */}
        <div style={s.card}>
          <h2 style={s.ct}>Features</h2>
          <label style={s.toggleRow}>
            <div>
              <div style={s.toggleLabel}>Waitlist</div>
              <div style={s.toggleHint}>When fully booked, the AI will offer customers a spot on the waitlist. You manage it from the Waitlist tab.</div>
            </div>
            <div
              style={{ ...s.toggle, background: settings.waitlist_enabled === 'true' ? '#4f46e5' : '#e2e8f0' }}
              onClick={() => setField('waitlist_enabled', settings.waitlist_enabled === 'true' ? 'false' : 'true')}
            >
              <div style={{ ...s.toggleThumb, transform: settings.waitlist_enabled === 'true' ? 'translateX(22px)' : 'translateX(2px)' }} />
            </div>
          </label>
          <div style={{ height: 1, background: '#f1f5f9', margin: '14px 0' }} />
          <label style={s.toggleRow}>
            <div>
              <div style={s.toggleLabel}>Floor plan AI mode</div>
              <div style={s.toggleHint}>When enabled, the AI uses your floor plan to assign tables based on location, window seats, and layout. Requires a complete floor plan.</div>
            </div>
            <div
              style={{ ...s.toggle, background: settings.floor_plan_ai_enabled === 'true' ? '#4f46e5' : '#e2e8f0' }}
              onClick={() => setField('floor_plan_ai_enabled', settings.floor_plan_ai_enabled === 'true' ? 'false' : 'true')}
            >
              <div style={{ ...s.toggleThumb, transform: settings.floor_plan_ai_enabled === 'true' ? 'translateX(22px)' : 'translateX(2px)' }} />
            </div>
          </label>
          <div style={{ height: 1, background: '#f1f5f9', margin: '14px 0' }} />
          <label style={s.toggleRow}>
            <div>
              <div style={s.toggleLabel}>Allow cross-area table combining</div>
              <div style={s.toggleHint}>When enabled, the AI may combine tables from different areas (e.g. indoor + outdoor) to fit a large party. Disable to keep areas separate.</div>
            </div>
            <div
              style={{ ...s.toggle, background: settings.allow_cross_location_combining !== 'false' ? '#4f46e5' : '#e2e8f0' }}
              onClick={() => setField('allow_cross_location_combining', settings.allow_cross_location_combining !== 'false' ? 'false' : 'true')}
            >
              <div style={{ ...s.toggleThumb, transform: settings.allow_cross_location_combining !== 'false' ? 'translateX(22px)' : 'translateX(2px)' }} />
            </div>
          </label>
        </div>

        {/* Tab lock */}
        <div style={s.card}>
          <h2 style={s.ct}>Tab lock</h2>
          <p style={s.hint}>Set an owner password to lock specific tabs. Staff can still use unlocked tabs freely — locked ones require the owner password to open each session.</p>

          {/* Owner password form */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
              {hasOwnerPwd ? 'Change owner password' : 'Set owner password'}
            </div>
            {pwdError && <div style={{ ...s.error, marginBottom: 12 }}>{pwdError}</div>}
            {pwdOk    && <div style={{ ...s.ok,    marginBottom: 12 }}>Owner password {hasOwnerPwd ? 'updated' : 'set'}!</div>}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {hasOwnerPwd && (
                <div style={{ flex: 1, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Current password</label>
                  <input style={s.input} className="glass-input" type="password" value={ownerPwdForm.current}
                    onChange={e => setOwnerPwdForm(f => ({ ...f, current: e.target.value }))} />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>New password</label>
                <input style={s.input} className="glass-input" type="password" placeholder="Min. 4 characters" value={ownerPwdForm.next}
                  onChange={e => setOwnerPwdForm(f => ({ ...f, next: e.target.value }))} />
              </div>
              <div style={{ flex: 1, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Confirm password</label>
                <input style={s.input} className="glass-input" type="password" value={ownerPwdForm.confirm}
                  onChange={e => setOwnerPwdForm(f => ({ ...f, confirm: e.target.value }))} />
              </div>
              <button type="button" style={s.pwdBtn} onClick={saveOwnerPassword} disabled={pwdSaving}>
                {pwdSaving ? 'Saving…' : hasOwnerPwd ? 'Update password' : 'Set password'}
              </button>
            </div>
          </div>

          {/* Locked tabs */}
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Tabs that require password
          </div>
          {!hasOwnerPwd && (
            <p style={{ fontSize: 13, color: '#94a3b8', margin: '0 0 12px' }}>Set an owner password above before selecting tabs to lock.</p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {LOCKABLE_TABS.map(({ key, label }) => {
              const locked = lockedTabsArray().includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  disabled={!hasOwnerPwd}
                  onClick={() => toggleLockedTab(key)}
                  style={{
                    padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: hasOwnerPwd ? 'pointer' : 'not-allowed',
                    border: locked ? '1.5px solid #4f46e5' : '1.5px solid #e2e8f0',
                    background: locked ? '#eef2ff' : '#f8fafc',
                    color: locked ? '#4f46e5' : '#64748b',
                    opacity: hasOwnerPwd ? 1 : 0.5,
                  }}
                >
                  {locked ? '🔒 ' : ''}{label}
                </button>
              );
            })}
          </div>
          <p style={{ fontSize: 12, color: '#94a3b8', margin: '12px 0 0' }}>Changes to locked tabs are saved with the main Save button below.</p>
        </div>

        {/* Email */}
        <div style={s.card}>
          <h2 style={s.ct}>Email confirmations (SMTP)</h2>
          <p style={s.hint}>When configured, customers receive a confirmation email after booking. Leave blank to disable.</p>
          <div style={s.row}>
            <Field label="SMTP host"><input style={s.input} className="glass-input" type="text" placeholder="smtp.gmail.com" value={settings.smtp_host||''} onChange={e=>setField('smtp_host',e.target.value)} /></Field>
            <Field label="Port"><input style={s.input} className="glass-input" type="number" placeholder="587" value={settings.smtp_port||587} onChange={e=>setField('smtp_port',e.target.value)} /></Field>
          </div>
          <div style={s.row}>
            <Field label="Username / email"><input style={s.input} className="glass-input" type="text" autoComplete="off" value={settings.smtp_user||''} onChange={e=>setField('smtp_user',e.target.value)} /></Field>
            <Field label="Password / app password"><input style={s.input} className="glass-input" type="password" autoComplete="new-password" value={settings.smtp_pass||''} onChange={e=>setField('smtp_pass',e.target.value)} /></Field>
          </div>
          <Field label="From address (optional)"><input style={{...s.input,maxWidth:320}} className="glass-input" type="email" placeholder="bookings@myrestaurant.com" value={settings.smtp_from||''} onChange={e=>setField('smtp_from',e.target.value)} /></Field>
        </div>

        <button type="submit" style={{...s.submit, ...(!hasChanges || saving ? {opacity:0.45, cursor:'not-allowed'} : {})}} className="glass-btn t" disabled={saving || !hasChanges}>
          {saving ? 'Saving…' : 'Save settings →'}
        </button>
      </form>

      {/* Confirm lock password modal */}
      {showLockConfirm && (
        <div style={s.overlay} onClick={() => setShowLockConfirm(false)}>
          <div style={s.confirmBox} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>Confirm with owner password</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16, lineHeight: 1.5 }}>You're changing which tabs are locked. Enter your owner password to confirm.</div>
            {confirmLockErr && <div style={{ ...s.error, marginBottom: 12 }}>{confirmLockErr}</div>}
            <input
              style={{ ...s.input, marginBottom: 14 }}
              type="password"
              autoFocus
              autoComplete="new-password"
              placeholder="Owner password"
              value={confirmLockPwd}
              onChange={e => { setConfirmLockPwd(e.target.value); setConfirmLockErr(''); }}
              onKeyDown={e => { if (e.key === 'Enter') confirmAndSave(); }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={s.submit} onClick={confirmAndSave} disabled={confirmSaving}>
                {confirmSaving ? 'Checking…' : 'Confirm & save'}
              </button>
              <button style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 24px', fontSize: 14, color: '#475569', cursor: 'pointer', border: 'none' }} onClick={() => setShowLockConfirm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({label,children}) {
  return (
    <div style={{display:'flex',flexDirection:'column',gap:6,flex:1,minWidth:200,marginBottom:16}}>
      <label style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</label>
      {children}
    </div>
  );
}

const s = {
  h1:         { fontSize:28, fontWeight:800, color:'var(--text-primary)', margin:0, letterSpacing:'-0.5px' },
  sub:        { fontSize:14, color:'var(--text-muted)', marginTop:4, marginBottom:26 },
  form:       { maxWidth:720 },
  card:       { background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:20, padding:'22px 26px', marginBottom:14, boxShadow:'0 1px 4px rgba(15,23,42,0.06)' },
  ct:         { fontSize:11, fontWeight:700, color:'var(--text-muted)', margin:'0 0 16px', textTransform:'uppercase', letterSpacing:'0.08em' },
  hint:       { fontSize:13, color:'#94a3b8', margin:'-8px 0 16px' },
  row:        { display:'flex', gap:16, flexWrap:'wrap' },
  input:      { background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:10, padding:'10px 13px', fontSize:14, outline:'none', width:'100%', boxSizing:'border-box', color:'#0f172a', transition:'all 0.18s' },
  hoursGrid:  { display:'flex', flexDirection:'column', gap:10 },
  dayRow:     { display:'flex', alignItems:'center', gap:14 },
  dayName:    { width:96, fontSize:14, fontWeight:600, color:'var(--text-secondary)' },
  closedLabel:{ fontSize:13, color:'var(--text-muted)', display:'flex', alignItems:'center', width:80, flexShrink:0, cursor:'pointer' },
  timeIn:     { background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', color:'#0f172a' },
  error:      { background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca', borderRadius:10, padding:'12px 16px', fontSize:13, marginBottom:16 },
  ok:         { background:'#f0fdf4', color:'#15803d', border:'1px solid #bbf7d0', borderRadius:10, padding:'12px 16px', fontSize:13, marginBottom:16 },
  submit:     { background:'#4f46e5', border:'none', borderRadius:12, padding:'14px 32px', fontSize:15, fontWeight:700, cursor:'pointer', color:'#fff' },

  overlay:    { position:'fixed', inset:0, background:'rgba(15,23,42,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 },
  confirmBox: { background:'#fff', border:'1px solid #e2e8f0', borderRadius:20, padding:'28px 32px', width:400, maxWidth:'90vw', boxShadow:'0 20px 60px rgba(15,23,42,0.18)' },
  pwdBtn:     { background:'#4f46e5', border:'none', borderRadius:10, padding:'11px 20px', fontSize:13, fontWeight:700, color:'#fff', cursor:'pointer', flexShrink:0, alignSelf:'flex-end' },
  addQBtn:    { background:'none', border:'1px dashed #cbd5e1', borderRadius:8, padding:'8px 16px', fontSize:13, color:'#64748b', cursor:'pointer' },
  removeQBtn: { background:'none', border:'none', fontSize:20, color:'#94a3b8', cursor:'pointer', lineHeight:1, padding:'0 4px', flexShrink:0 },
  toggleRow:  { display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:20, cursor:'default' },
  toggleLabel:{ fontSize:14, fontWeight:600, color:'#0f172a', marginBottom:3 },
  toggleHint: { fontSize:13, color:'#64748b', lineHeight:1.5 },
  toggle:     { width:46, height:26, borderRadius:13, position:'relative', cursor:'pointer', flexShrink:0, marginTop:2, transition:'background 0.2s' },
  toggleThumb:{ position:'absolute', top:2, width:22, height:22, background:'#fff', borderRadius:'50%', boxShadow:'0 1px 4px rgba(0,0,0,0.2)', transition:'transform 0.2s' },
};
